<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('summaryTrackingReport.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('summaryTrackingReport.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <ts-loading-banner :loading="loading">
          <div class="ts-flex ts-items-end ts-space-x-3">
            <div class="">
              <label>{{ $t('summaryTrackingReport.date') }}</label
              ><br />
              <a-range-picker
                v-model="model.dateRange"
                :allowClear="false"
                format="DD-MM-YYYY HH:mm:ss"
                valueFormat="DD-MM-YYYY HH:mm:ss"
                :show-time="{ defaultValue: moment('00:00:00', 'HH:mm:ss') }"
              />
            </div>
            <div class="ts-w-64">
              <label>{{ $t('summaryTrackingReport.groupName') }}</label>
              <a-select
                class="ts-w-64"
                v-model="model.group_id"
                :options="groups"
                showSearch
                :filterOption="filterOption"
                :placeholder="$t('summaryTrackingReport.groupName')"
                @change="clearOnChangeGroup"
              ></a-select>
              <div class="ts-text-red-600" v-if="errors.has('group_id')">
                {{ errors.first('group_id') }}
              </div>
            </div>
            <div class="ts-w-64">
              <label>{{ $t('summaryTrackingReport.employee') }}</label>
              <a-select
                class="ts-w-64"
                v-model="model.employee_id"
                :options="employees"
                showSearch
                :filterOption="filterOption"
                :placeholder="$t('summaryTrackingReport.employee')"
              ></a-select>
              <div class="ts-text-red-600" v-if="errors.has('employee_id')">
                {{ errors.first('employee_id') }}
              </div>
            </div>
            <ts-button
              color="primary"
              outline
              class="ts-h-8"
              @click.prevent="onFetch"
              >{{ $t('summaryTrackingReport.filter') }}</ts-button
            >
            <ts-button
              color="info"
              outline
              class="ts-h-8"
              @click.prevent="onExportExcel"
            >
              <i class="fas fa-file-excel"></i>
              {{ $t('excel') }}</ts-button
            >
          </div>

          <div class="overflow-auto ts-mt-3">
            <ts-table
              :columns="columns"
              :records="resources"
              :loading="loading"
            >
              <template v-slot="{ record }">
                <td>{{ record.user_name }}</td>
                <td>{{ record.sale_position }}</td>
                <td>{{ record.group_name }}</td>
                <td>{{ record.phone }}</td>
                <td>
                  {{
                    moment(record.min_date, 'DD-MM-YYYY HH:mm:ss').format(
                      'DD-MM-YYYY hh:mm A'
                    )
                  }}
                  -
                  {{
                    moment(record.max_date, 'DD-MM-YYYY HH:mm:ss').format(
                      'hh:mm A'
                    )
                  }}
                </td>
                <td>{{ record.duration }}</td>
                <td>{{ record.remark }}</td>
                <td class="ts-text-center ts-space-x-3">
                  <a
                    href="#"
                    v-tooltip="$t('edit')"
                    class="text-primary"
                    @click.prevent="onClickEdit(record)"
                  >
                    <i class="fas fa-edit"></i>
                  </a>
                </td>
              </template>
            </ts-table>
          </div>
          <div class="d-flex justify-content-end p-15">
            <ts-pagination v-model="pagination" @navigate="onFetch">
            </ts-pagination>
          </div>
        </ts-loading-banner>
      </ts-card-wrapper>
    </ts-card>
  </div>
</template>

<script>
import moment from 'moment'
import { uniqBy, trim } from 'lodash'
import { mapActions, mapState } from 'vuex'
import { Errors } from 'form-backend-validation'
import * as FileDownload from 'downloadjs'

export default {
  name: 'summaryTrackingReport',
  data() {
    return {
      loading: false,
      errors: new Errors(),
      resourcesGroup: [],
      model: {
        dateRange: [
          moment()
            .startOf('days')
            .format('DD-MM-YYYY HH:mm:ss'),
          moment()
            .endOf('days')
            .format('DD-MM-YYYY HH:mm:ss')
        ],
        group_id: '',
        employee_id: ''
      }
    }
  },
  created() {
    this.fetchGroup()
  },
  computed: {
    ...mapState('sale/locationTrackingRemark', ['resources', 'pagination']),
    groups() {
      let list = [
        {
          label: 'All',
          value: ''
        }
      ]
      this.resourcesGroup.forEach(element => {
        list.push({
          label: element.group_name,
          value: element.id
        })
      })
      return list
    },
    employees() {
      let list = [
        {
          label: 'All',
          value: ''
        }
      ]
      const emp = [
        ...this.resourcesGroup
          .filter(
            r => r.id === this.model.group_id || this.model.group_id == ''
          )
          .flatMap(element => element.group_user)
      ]
      emp
        .filter(e => e.employee_id !== null)
        .forEach(el => {
          list.push({
            label: el.user_name,
            value: el.employee_id
          })
        })
      return uniqBy(list, 'value')
    },
    columns() {
      return [
        { name: this.$t('summaryTrackingReport.userName') },
        { name: this.$t('summaryTrackingReport.position') },
        { name: this.$t('summaryTrackingReport.groupName') },
        { name: this.$t('summaryTrackingReport.phoneNumber') },
        { name: this.$t('summaryTrackingReport.dateTime') },
        { name: this.$t('summaryTrackingReport.duration') },
        { name: this.$t('summaryTrackingReport.remark') },
        { name: this.$t('actions'), style: 'text-align:center; width:7%' }
      ]
    }
  },
  methods: {
    ...mapActions('sale/locationTrackingRemark', [
      'fetch',
      'getUserGroup',
      'exportExcel'
    ]),
    moment,
    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text
          .toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      )
    },
    onFetch(attributes = {}) {
      this.loading = true
      this.errors = new Errors()
      const model = localStorage.getItem('summaryTrackingReportModel')

      if (model) {
        this.model = JSON.parse(model)
      }

      this.fetch({ ...this.model, ...attributes })
        .then(() => {
          localStorage.removeItem('summaryTrackingReportModel')
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.loading = false))
    },
    fetchGroup() {
      this.loading = true
      this.getUserGroup()
        .then(group => {
          this.resourcesGroup = group.data
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => (this.loading = false))
    },
    clearOnChangeGroup() {
      this.model.employee_id = ''
    },
    onClickEdit(record) {
      localStorage.setItem(
        'summaryTrackingReportModel',
        JSON.stringify(this.model)
      )
      this.$router.push({
        name: 'location-tracking',
        query: {
          remark_id: record.remark_id,
          min_date: record.min_date,
          max_date: record.max_date,
          employee_id: record.employee_id
        }
      })
    },
    onExportExcel() {
      this.loading = true
      this.errors = new Errors()
      this.exportExcel(this.model)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.loading = false))
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.onFetch()
    })
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('sale/locationTrackingRemark/RESET_STATE')

    next()
  }
}
</script>
